'use client'

import { CustomDataField } from '@repo/custom-data-pages/CustomDataField'
import { EmptyState } from '@repo/ui/EmptyState'
import { LargeCustomDataPageIcon } from '@repo/ui/LargeCustomDataPageIcon'
import { LoadingState } from '@repo/ui/LoadingState'
import { useCustomDataPageStore } from '@repo/store/useCustomDataPageStore'
import { formatDate, parseDate } from '@repo/utils/time'
import { useState, useEffect } from 'react'
import { ValidationErrorAlert } from '@repo/ui/ValidationErrorAlert'
import MUIStack from '@mui/material/Stack'
import PropTypes from 'prop-types'

export const ExtensionsWizardForm = ({
  testData,
  level = 'matter',
  workTypeId,
  clientType = 'I',
  fields = [],
  setFields = () => {},
  values = {},
  setValues = () => {},
  validationErrors = {},
  overrideDefaultValues = {}
}) => {
  const { fetchMatterExtensions, fetchClientExtensions, fetchListValues, getDefaultValueForCustomDataField } =
    useCustomDataPageStore()
  const [isFetchingData, setIsFetchingData] = useState(false)

  useEffect(() => {
    const getMatterExtensions = async () => {
      setIsFetchingData(true)
      const extensions =
        level === 'matter' ? await fetchMatterExtensions({ workTypeId }) : await fetchClientExtensions({ clientType })

      const fields = extensions?.flatMap((extension) => extension.fields) || []

      const customDataListIds =
        fields.filter((field) => field.fieldType === 'List').map((field) => field.customDataFieldListId) || []

      if (customDataListIds.length) {
        await fetchListValues({ customDataListIds })
      }

      const valuesWithDefaults = { ...values }
      const defaultValues = {}

      fields.forEach((field) => {
        const { fieldId } = field

        if (values[fieldId] === null || values[fieldId] === undefined) {
          // we might want to override the default value with a value from the overrideDefaultValues object, e.g. from a Third Party Notification
          if (overrideDefaultValues[fieldId]) {
            let overrideValue = overrideDefaultValues[fieldId]
            if (field.fieldType === 'Date') {
              const parsed = parseDate(overrideValue, 'dd/MM/yyyy')
              if (parsed && !Number.isNaN(parsed.getTime())) overrideValue = formatDate(parsed, 'yyyy-MM-dd')
            }
            valuesWithDefaults[fieldId] = overrideValue
            defaultValues[fieldId] = overrideValue
          } else {
            const defaultValue = getDefaultValueForCustomDataField({ field })
            if (defaultValue !== null) {
              valuesWithDefaults[fieldId] = defaultValue
              defaultValues[fieldId] = defaultValue
            }
          }
        }
      })

      setFields(fields)
      console.log({ defaultValues, valuesWithDefaults })
      setValues({ ...valuesWithDefaults })
      setIsFetchingData(false)
    }

    if (!testData) getMatterExtensions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchMatterExtensions, fetchClientExtensions, level, testData, workTypeId, fetchListValues, clientType])

  const onUpdateValue = ({ fieldId, value }) => {
    setValues({
      ...values,
      [fieldId]: value
    })
  }

  return (
    <MUIStack
      direction="column"
      gap={3}
      data-testid="ExtensionsWizardForm"
      sx={{
        width: '100%',
        '.MuiTypography-h6': {
          width: '100%',
          fontFamily: 'Mulish',
          fontWeight: 400,
          marginBottom: '4px !important',
          fontSize: '0.75rem'
        }
      }}
    >
      {isFetchingData ? <LoadingState /> : null}
      {!isFetchingData && !fields?.length ? (
        <EmptyState
          icon={<LargeCustomDataPageIcon sx={{ width: '60px', height: '60px', mb: 8, color2: '#3D517B' }} />}
          text="No extensions required. You can skip this step."
        />
      ) : null}

      <ValidationErrorAlert errorCount={Object.values(validationErrors).length || 0} isTop />

      {!isFetchingData && fields?.length
        ? fields.map((field) => {
            const { pageId, fieldId } = field
            return (
              <CustomDataField
                key={`${pageId}-${fieldId}`}
                field={field}
                value={values[fieldId] || null}
                onUpdateValue={({ value }) => onUpdateValue({ fieldId, value })}
                validationErrors={validationErrors[fieldId] || null}
                includeBorder={false}
                pageIsInEditMode
                valuesLoaded
                sx={{ gap: 0, padding: '12px', maxWidth: '600px' }}
              />
            )
          })
        : null}

      <ValidationErrorAlert errorCount={Object.values(validationErrors).length || 0} sx={{ mt: 8 }} />
    </MUIStack>
  )
}

ExtensionsWizardForm.propTypes = {
  testData: PropTypes.any,
  workTypeId: PropTypes.string,
  level: PropTypes.string,
  clientType: PropTypes.string,
  values: PropTypes.object,
  setValues: PropTypes.func,
  validationErrors: PropTypes.object,
  fields: PropTypes.array,
  setFields: PropTypes.func,
  overrideDefaultValues: PropTypes.object
}
